import { useState } from "react";
import { BudgetSummary } from "@/components/common/BudgetSummary";
import { Link, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { PageHeader } from "@/components/common/PageHeader";
import { ExportButtons } from "@/components/common/ExportButtons";
import { StatusBadge } from "@/components/common/StatusBadge";
import { KanbanBoard } from "@/components/tickets/KanbanBoard";
import { NewRequestDialog } from "@/components/tickets/NewRequestDialog";
import { PsychotestOrderDialog } from "@/pages/RacPsychotest";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { SERVICE_META, fmtDate } from "@/lib/constants";
import { cn } from "@/lib/utils";

function Kpi({ label, value, tone = "default", sub, testId }) {
  const tones = { default: "", red: "text-red-500", amber: "text-amber-500", green: "text-emerald-500", blue: "text-blue-500", purple: "text-purple-500" };
  return (
    <div className="op-card p-4" data-testid={testId}>
      <div className="data-label">{label}</div>
      <div className={cn("kpi-num mt-1", tones[tone])}>{value ?? "—"}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function AssignmentWidget() {
  const { data } = useFetch("/assignments/overview");
  if (!data) return null;
  return (
    <section className="op-card p-4" data-testid="dashboard-assignment-widget">
      <div className="mb-3 flex items-center justify-between">
        <div><h2 className="text-base font-semibold md:text-lg">Penugasan — siapa mengerjakan apa</h2><p className="text-xs text-muted-foreground">{data.summary.unassigned} request belum ditugaskan · {data.summary.active} ticket aktif di {data.summary.staff} staf</p></div>
        <Link to="/assignments" className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90" data-testid="goto-assignments-btn">{data.summary.unassigned ? `Tugaskan ${data.summary.unassigned} request` : "Buka Penugasan"}</Link>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {data.board.map((b) => (
          <div key={b.user.id} className={cn("rounded-md border p-3", b.overdue > 0 && "border-red-500/40")} data-testid={`widget-staff-${b.user.email}`}>
            <div className="flex items-center justify-between"><span className="text-sm font-semibold">{b.user.name}</span><span className="font-mono text-xs"><b className={b.active ? "text-blue-500" : "text-muted-foreground"}>{b.active}</b> aktif{b.overdue ? <b className="ml-2 text-red-500">{b.overdue} overdue</b> : null}</span></div>
            <ul className="mt-1.5 space-y-0.5 text-xs">
              {b.tickets.slice(0, 3).map((t) => <li key={t.id} className="flex items-center gap-1.5 truncate"><span className={cn("status-dot shrink-0", t.priority === "critical" ? "bg-rose-500" : t.is_overdue ? "bg-red-500" : t.status === "on_hold" ? "bg-purple-500" : "bg-amber-500")} /><Link to={`/tickets/${t.id}`} className="truncate hover:underline"><span className="font-mono opacity-70">{t.code}</span> {t.title}</Link></li>)}
              {b.tickets.length > 3 && <li className="text-muted-foreground">+{b.tickets.length - 3} lainnya</li>}
              {b.tickets.length === 0 && <li className="text-muted-foreground">Tersedia — tidak ada tugas aktif</li>}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { user, can } = useAuth();
  const nav = useNavigate();
  const { data: s, loading } = useFetch("/dashboard/summary");
  const { data: tickets } = useFetch(can("ga_staff", "supervisor", "asset_pic", "legal_admin") ? "/tickets?status=pending_approval,revise,open,in_progress,on_hold,resolved&limit=100" : "/tickets?mine=true&limit=100");
  const [pick, setPick] = useState(null);
  const [psy, setPsy] = useState(false);
  const k = s?.kpi || {};

  return (
    <div className="space-y-6 fade-up">
      <PageHeader title={`Halo, ${user.name.split(" ")[0]}`} subtitle="Ringkasan operasional General Affairs hari ini.">
        <ExportButtons collection="summary" custom="/export/summary/dashboard" />
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="kpi-grid">
        <Kpi label="Total Open Tickets" value={k.total_open} tone="blue" sub={`${k.pending_approval || 0} menunggu approval`} testId="kpi-open" />
        <Kpi label="In Progress" value={k.in_progress} tone="amber" sub={`${k.on_hold || 0} on hold`} testId="kpi-in-progress" />
        <Kpi label="Overdue SLA" value={k.overdue} tone={k.overdue ? "red" : "green"} sub="ticket melewati batas SLA" testId="kpi-overdue" />
        <Kpi label="Resolved / Closed" value={k.closed} tone="green" sub={`Avg resolusi ${k.avg_resolution_hours ?? 0} jam`} testId="kpi-closed" />
      </div>

      <BudgetSummary compact />
      <Separator className="my-2" data-testid="divider-kpi-cta" />

      <section>
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
          <h2 className="text-base font-semibold md:text-lg">Call to Action — Layanan GA</h2>
          <div className="flex gap-3 text-[11px] text-muted-foreground" data-testid="cta-legend">
            <span className="flex items-center gap-1"><span className="status-dot bg-blue-500" />Jumlah order / open ticket yang diajukan (hilang saat mulai dieksekusi)</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="call-to-action">
          {Object.entries(SERVICE_META).map(([key, m]) => {
            const Icon = Icons[m.icon] || Icons.Circle;
            const c = s?.cta_counts?.[key] || {};
            return (
              <button key={key} onClick={() => setPick(key)} data-testid={`cta-${key}`}
                className="group op-card relative p-4 text-left transition-all duration-200 hover:-translate-y-1 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10">
                <div className="absolute right-3 top-3" data-testid={`cta-counts-${key}`}>
                  <span title={`Open ticket diajukan: ${c.issued || 0}`} data-testid={`cta-${key}-issued`} className={cn("flex h-7 min-w-7 items-center justify-center rounded-full px-2 font-mono text-xs font-bold text-white shadow-sm", c.issued ? "bg-blue-500" : "bg-muted-foreground/25")}>{c.issued || 0}</span>
                </div>
                <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white"><Icon className="h-5 w-5" /></div>
                <div className="text-sm font-semibold">{m.title}</div>
                <div className="text-xs text-muted-foreground">{m.desc}</div>
              </button>
            );
          })}
          <button onClick={() => setPsy(true)} data-testid="cta-psychotest"
            className="group op-card relative border-dashed p-4 text-left transition-all duration-200 hover:-translate-y-1 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10">
            <div className="absolute right-3 top-3 rounded-full bg-primary/15 px-2 py-0.5 font-mono text-[10px] text-primary">RAC</div>
            <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white"><Icons.BrainCircuit className="h-5 w-5" /></div>
            <div className="text-sm font-semibold">Pemesanan Psikotes</div>
            <div className="text-xs text-muted-foreground">Psikotest Service RAC — peserta, jadwal, pembayaran</div>
          </button>
        </div>
      </section>

      <Separator className="my-2" data-testid="divider-cta-summary" />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="op-card p-4" data-testid="sla-status-card">
          <div className="data-label mb-3">SLA Status (tiket aktif)</div>
          {[["ok", "Dalam SLA", "bg-emerald-500"], ["warning", "Mendekati batas", "bg-amber-500"], ["breached", "Overdue", "bg-red-500"]].map(([key, label, c]) => {
            const total = Object.values(s?.sla_state || {}).reduce((a, b) => a + b, 0) || 1;
            const v = s?.sla_state?.[key] || 0;
            return (
              <div key={key} className="mb-2">
                <div className="flex justify-between text-xs"><span>{label}</span><span className="font-mono">{v}</span></div>
                <div className="h-1.5 rounded-full bg-muted"><div className={cn("h-1.5 rounded-full transition-all", c)} style={{ width: `${(v / total) * 100}%` }} /></div>
              </div>
            );
          })}
          <div className="mt-3 border-t pt-3 data-label">Tickets by Module</div>
          <ul className="mt-2 space-y-1 text-xs">{(s?.by_service || []).slice(0, 6).map((x) => <li key={x.id} className="flex justify-between"><span>{x.label}</span><span className="font-mono">{x.count}</span></li>)}</ul>
        </div>
        <div className="op-card p-4" data-testid="doc-reminder-card">
          <div className="mb-3 flex items-center justify-between"><span className="data-label">Permintaan Akomodasi (Hotel) — Pemberangkatan</span><Link to="/departures" className="text-xs text-primary hover:underline">Lihat</Link></div>
          {(s?.accommodation_requests || []).length === 0 && <p className="text-xs text-muted-foreground" data-testid="accommodation-empty">Tidak ada permintaan hotel aktif.</p>}
          <ul className="space-y-2" data-testid="accommodation-list">
            {(s?.accommodation_requests || []).slice(0, 4).map((a) => (
              <li key={a.id} className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs" data-testid={`accommodation-${a.code}`}>
                <div className="flex items-center justify-between gap-2"><Link to={`/tickets/${a.id}`} className="truncate font-medium hover:underline">{a.title}</Link><span className="shrink-0 rounded bg-amber-500/20 px-1.5 py-0.5 font-mono text-[10px] text-amber-700 dark:text-amber-300">Hotel</span></div>
                <div className="font-mono text-muted-foreground">{a.code} · {a.students} siswa · {a.destination_country || "—"} · {fmtDate(a.departure_at)}</div>
                {a.hotel_notes && <div className="mt-0.5 truncate text-muted-foreground">{a.hotel_notes}</div>}
              </li>
            ))}
          </ul>
          <div className="mt-3 mb-3 flex items-center justify-between border-t pt-3"><span className="data-label">Reminder Dokumen</span><Link to="/documents" className="text-xs text-primary hover:underline">Lihat semua</Link></div>
          {(s?.expiring_documents || []).length === 0 && <p className="text-xs text-muted-foreground">Tidak ada dokumen mendekati expired.</p>}
          <ul className="space-y-2">
            {(s?.expiring_documents || []).map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-2 text-xs">
                <div className="min-w-0"><div className="truncate font-medium">{d.title}</div><div className="font-mono text-muted-foreground">{d.code} · {fmtDate(d.expiry_date)}</div></div>
                <StatusBadge status={d.expiry_state} />
              </li>
            ))}
          </ul>
        </div>
        <div className="op-card p-4" data-testid="ops-summary-card">
          <div className="data-label mb-3">Aset · Kendaraan · Maintenance</div>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[["Aset", s?.assets, "/assets"], ["Kendaraan", s?.vehicles, "/vehicles"], ["Fasilitas", s?.facilities, "/facilities"]].map(([l, v, to]) => (
              <Link to={to} key={l} className="rounded-md border p-2 hover:bg-accent"><div className="kpi-num text-2xl">{v?.total ?? 0}</div><div className="text-[11px] text-muted-foreground">{l}</div><div className="mt-1 font-mono text-[10px] text-purple-500">{v?.maintenance || 0} maint.</div></Link>
            ))}
          </div>
          <div className="mt-3 border-t pt-3 flex items-center justify-between"><span className="data-label">Work Orders</span><Link to="/maintenance" className="text-xs text-primary hover:underline">Kelola</Link></div>
          <div className="mt-2 flex gap-4 text-xs"><span>Upcoming <b className="font-mono">{s?.maintenance?.upcoming ?? 0}</b></span><span className="text-red-500">Overdue <b className="font-mono">{s?.maintenance?.overdue ?? 0}</b></span><span>In progress <b className="font-mono">{s?.maintenance?.in_progress ?? 0}</b></span></div>
          <ul className="mt-2 space-y-1 text-xs">{(s?.maintenance?.next || []).map((w) => <li key={w.id} className="flex justify-between"><span className="truncate">{w.title}</span><span className="font-mono text-muted-foreground">{fmtDate(w.scheduled_date)}</span></li>)}</ul>
        </div>
      </div>

      <Separator className="my-2" data-testid="divider-summary-kanban" />

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold md:text-lg">Kanban Ticket Operasional</h2>
          <Button variant="outline" size="sm" onClick={() => nav("/tickets")} data-testid="goto-tickets-btn">Tabel & filter</Button>
        </div>
        {loading && !tickets ? <div className="text-sm text-muted-foreground">Memuat...</div> : <KanbanBoard tickets={tickets || []} />}
      </section>
      {can("supervisor", "ga_staff", "asset_pic", "legal_admin") && <AssignmentWidget />}
      <NewRequestDialog open={!!pick} onOpenChange={(o) => !o && setPick(null)} preset={pick} onCreated={(t) => nav(`/tickets/${t.id}`)} />
      <PsychotestOrderDialog open={psy} onOpenChange={setPsy} onCreated={() => nav("/rac/psychotest")} />
    </div>
  );
}
